import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from "vue";
import { downloadExcelFile, ITableHeader, showServerError } from "@/utils";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BasePageTabs, { ITab } from "@/components-UI/BasePageTabs.vue";
import { CellsType, prepareItems, RawConfig } from "@/utils/CellsFactory";
import { IAffiliateInvoice, IAffiliateInvoiceAffiliates, IAffiliateInvoiceOffers } from "@/api/types/payout";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import DownloadButton from "@/components-UI/buttons/DownloadButton.vue";
import { IParamsObject } from "@/api/types";
import { getMasterPaymentDetailsFile } from "@/api/payout";

interface Props {
    invoice: IAffiliateInvoice;
}

type TABS = keyof typeof TABS;


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoiceLeadsTable',
  props: {
    invoice: null
  },
  setup(__props: any) {

const props = __props as {
    invoice: IAffiliateInvoice;
}



onMounted(() => {
    formatItems();
});

const isMaster = computed(() => !!props.invoice.is_master ?? false);
const rawOfferItems = computed(() => props.invoice.items ?? []);
const rawAffiliateItems = computed(() => props.invoice.affiliates ?? []);

// --TABS
const TABS = {
    OFFER: 'OFFER',
    AFFILIATE: 'AFFILIATE',
} as const;

const tabs = computed<ITab[]>(() => {
    return [
        { text: 'Офферы', value: TABS.OFFER },
        { text: 'Партнёры', value: TABS.AFFILIATE, disabled: !isMaster.value },
    ];
});

const currentTab = ref<TABS>(TABS.OFFER);

const changeTab = (tab: TABS) => {
    currentTab.value = tab;
    offer.value = '';
    affiliate.value = '';
    formatItems();
};

// --SELECT
const offer = ref('');
const affiliate = ref('');

const offerSelectItems = computed(() => {
    return rawOfferItems.value.map((item: IAffiliateInvoiceOffers) => ({
        value: item.offer_id,
        label: item.name,
    }));
});

const affiliateSelectItems = computed(() => {
    return rawAffiliateItems.value.map((item: IAffiliateInvoiceAffiliates) => ({
        value: item.affiliate_id,
        label: item.affiliate_name,
    }));
});

const getSelectOptions = computed(() => {
    const isOffer = currentTab.value === TABS.OFFER;
    return {
        value: isOffer ? offer.value : affiliate.value,
        items: isOffer ? offerSelectItems.value : affiliateSelectItems.value,
        placeholder: isOffer ? 'Выберите оффер' : 'Выберите партнёра',
        template: (i) => `${i.value} ${i.label}`,
        view: 'outlined',
        itemValue: 'value',
        itemLabel: 'label',
        clearable: true,
        filterable: true,
    };
});

const updateSelectValue = (value: number) => {
    if (currentTab.value === TABS.OFFER) {
        offer.value = value;
    } else {
        affiliate.value = value;
    }
};

// -- HEADERS
const offerHeaders: ITableHeader[] = [
    { text: 'ID', value: 'id', sortable: true, width: '80px' },
    { text: 'ID оффера', value: 'offer_id', sortable: true, width: '80px', align: 'center' },
    { text: 'Название оффера', value: 'name', sortable: true, width: 'auto' },
    { text: 'ID цели', value: 'goal_id', sortable: true, width: '80px', align: 'center' },
    { text: 'Количество', value: 'leads_num', sortable: true, align: 'right' },
    { text: 'Доход', value: 'revenue', sortable: true, align: 'right', width: 'max-content' },
    { text: 'Выплата', value: 'amount', sortable: true, align: 'right', width: 'max-content' },
    { text: 'Прибыль', value: 'profit', sortable: true, align: 'right', width: 'max-content' },
];

const affiliateHeaders: ITableHeader[] = [
    { text: 'ID партнёра', value: 'affiliate_id', sortable: true, width: '80px', align: 'center' },
    { text: 'Имя партнёра', value: 'affiliate_name', sortable: true, width: 'auto'  },
    { text: 'Количество', value: 'leads_num', sortable: true, align: 'center' },
    { text: 'Доход', value: 'revenue', sortable: true, align: 'right', width: 'max-content'  },
    { text: 'Выплата', value: 'payout', sortable: true, align: 'right', width: 'max-content'  },
    { text: 'Прибыль', value: 'profit', sortable: true, align: 'right', width: 'max-content'  },
];

const getHeaders = computed(() => {
    return currentTab.value === TABS.OFFER ? offerHeaders : affiliateHeaders;
});

const offerColumns = computed(() => offerHeaders.map(h => h.value));
const affiliateColumns = computed(() => affiliateHeaders.map(h => h.value));

// -- ITEMS
const offerConfig = ref<RawConfig<IAffiliateInvoiceOffers>>({
    [CellsType.Route]: {
        offer_id: {
            name: 'offer',
            valueKey: 'offer_id',
            isChip: true,
            text: 'offer_id',
        },
    },
    [CellsType.Number]: {
        revenue: { isMono: true },
        amount: { isMono: true },
        profit: { isMono: true },
    },
    [CellsType.Text]: {
        goal_id: {
            template: (i: IAffiliateInvoiceOffers) => String(i.goal_id),
        },
    },
});

const affiliateConfig = ref<RawConfig<IAffiliateInvoiceAffiliates>>({
    [CellsType.Route]: {
        affiliate_id: {
            name: 'affiliate',
            valueKey: 'affiliate_id',
            isChip: true,
            text: 'affiliate_id',
        },
    },
    [CellsType.Number]: {
        revenue: { isMono: true },
        payout: { isMono: true },
        profit: { isMono: true },
    },
});

const offerItems = ref([]);
const affiliateItems = ref([]);

const getItems = computed(() => {
    if (currentTab.value === TABS.OFFER) {
        if (offer.value === '') return offerItems.value;
        return offerItems.value.filter((item) => item.offer_id.componentData.value === offer.value);
    } else {
        if (affiliate.value === '') return affiliateItems.value;
        return affiliateItems.value.filter((item) => item.affiliate_id.componentData.value === affiliate.value);
    }
});

const formatItems = () => {
    if (currentTab.value === TABS.OFFER) {
        if (offerItems.value.length) return;
        offerItems.value = formatOfferItems();
    } else {
        if (affiliateItems.value.length) return;
        affiliateItems.value = formatAffiliateItems();
    }
};

const formatOfferItems = () => {
    return prepareItems(rawOfferItems.value, offerConfig.value, offerColumns.value);
};

const formatAffiliateItems = () => {
    return prepareItems(rawAffiliateItems.value, affiliateConfig.value, affiliateColumns.value);
};

const isExportLoading = ref(false);

const downloadReportDetails = async (): Promise<void> => {
    try {
        isExportLoading.value = true;
        const isOffer = currentTab.value === TABS.OFFER;
        const reportType = isOffer ? 'offers' : 'affiliates';
        const key = isOffer ? 'offer_id' : 'affiliate_id';
        const filterValue = isOffer ? offer.value : affiliate.value;
        const params: IParamsObject = filterValue ? { [key]: filterValue } : {};

        const report = await getMasterPaymentDetailsFile(props.invoice.id, reportType, params);
        downloadExcelFile(report, `${props.invoice.id} - детализация по счёту (${isOffer ? 'офферы' : 'партнёры'})`, 'xls', true);
    } catch (err) {
        showServerError(err, 'Ошибка загрузки файла');
    } finally {
        isExportLoading.value = false;
    }
};


return { __sfc: true,props, isMaster, rawOfferItems, rawAffiliateItems, TABS, tabs, currentTab, changeTab, offer, affiliate, offerSelectItems, affiliateSelectItems, getSelectOptions, updateSelectValue, offerHeaders, affiliateHeaders, getHeaders, offerColumns, affiliateColumns, offerConfig, affiliateConfig, offerItems, affiliateItems, getItems, formatItems, formatOfferItems, formatAffiliateItems, isExportLoading, downloadReportDetails, BaseTable, BaseEditableCard, BasePageTabs, BaseSelect, DownloadButton }
}

})